import { onlineManager, QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { useConnection } from '@/stores/connection';
import { MAX_AGE_MS } from '@/stores/queryCache';

/**
 * The query cache's idea of "offline" is the app's own: the browser saying so,
 * or the server not answering (src/stores/connection.ts). Left to itself it
 * would only follow the browser, so a stopped server and a dead line behaved
 * differently — and the screens could not tell which they were in.
 */
onlineManager.setEventListener((setOnline) => {
  setOnline(useConnection.getState().online);
  return useConnection.subscribe((state, previous) => {
    if (state.online !== previous.online) setOnline(state.online);
  });
});

/**
 * The one query cache the app has. Its own module, rather than made in
 * `main.tsx`, because the copy of a person's work kept on the PC fills it when
 * the copy opens and empties it when the copy closes (.docs/023).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Kept as long as the copy keeps them. With the default five minutes,
      // what the warm-up loaded for screens not open was dropped from memory,
      // and so from the copy, long before anyone needed it offline.
      gcTime: MAX_AGE_MS,
      staleTime: 30_000,
      // Once more for a server fault; never for no line — that waits, paused,
      // for the line to come back, showing what was loaded before. "Too busy"
      // (429) is waited out a few times, as long as the server asks.
      retry: (failures, error) => {
        if (error instanceof ApiError && error.status === 0) return false;
        if (error instanceof ApiError && error.status === 429) return failures < 4;
        return failures < 1;
      },
      retryDelay: (failures, error) =>
        error instanceof ApiError && error.status === 429
          ? Math.min(error.retryAfterMs ?? 10_000, 60_000)
          : Math.min(1000 * 2 ** failures, 30_000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Always run. Offline, TanStack's default holds a mutation back without
      // a word, so "Save" did nothing at all — when every stock save is meant
      // to be kept on this PC by the outbox, and anything else to say plainly
      // that it needs the connection.
      networkMode: 'always',
    },
  },
});
